/**
 * Application state and a minimal observable store.
 *
 * The app is a simple sequential state machine (one screen at a time), so a
 * hand-rolled store with a subscribe/notify pattern is sufficient and keeps
 * the whole UI framework-free. Screens read state, dispatch changes, and the
 * root re-renders.
 */

import type { TopicId } from "../curriculum/curriculum";
import { TaughtSet, TOPICS } from "../curriculum/curriculum";
import type { Difficulty } from "../topics/types";

export type ScreenName = "home" | "learn" | "practice" | "quiz" | "progress";

export interface Settings {
  currentTopic: TopicId;
  /** Topic ids marked as taught (drives review gating). */
  taughtIds: TopicId[];
}

export interface AppState {
  screen: ScreenName;
  settings: Settings;
  /** Transient per-screen params (e.g. a chosen topic/difficulty), opaque. */
  screenParams: Record<string, unknown>;
}

/**
 * Default starting topic. Integer operations (6.PAFR.3.5) is the first
 * generator-backed skill in the algebra/PAFR strand and the natural entry
 * point for the year's core arithmetic-into-algebra work.
 */
const DEFAULT_CURRENT: TopicId = "integer-operations";

/**
 * V1 default: every topic is marked taught.
 *
 * The `TaughtSet` gating machinery stays in place for a future teacher/parent
 * flow that restricts review to material the class has reached. For a single
 * self-directed student who controls their own Current Topic, gating behind a
 * manual "mark taught" step only adds friction, so mixed review sees the whole
 * curriculum out of the box.
 */
export function defaultState(): AppState {
  return {
    screen: "home",
    settings: {
      currentTopic: DEFAULT_CURRENT,
      taughtIds: TOPICS.map((t) => t.id),
    },
    screenParams: {},
  };
}

type Listener = (state: AppState) => void;

export class Store {
  private state: AppState;
  private listeners: Listener[] = [];

  constructor(initial: AppState) {
    this.state = initial;
  }

  get(): AppState {
    return this.state;
  }

  taughtSet(): TaughtSet {
    return new TaughtSet(this.state.settings.taughtIds);
  }

  navigate(screen: ScreenName, params: Record<string, unknown> = {}): void {
    this.state = { ...this.state, screen, screenParams: params };
    this.notify();
  }

  setCurrentTopic(topic: TopicId): void {
    this.state = {
      ...this.state,
      settings: { ...this.state.settings, currentTopic: topic },
    };
    this.notify();
  }

  setTaught(ids: TopicId[]): void {
    this.state = {
      ...this.state,
      settings: { ...this.state.settings, taughtIds: [...ids] },
    };
    this.notify();
  }

  patchSettings(patch: Partial<Settings>): void {
    this.state = {
      ...this.state,
      settings: { ...this.state.settings, ...patch },
    };
    this.notify();
  }

  subscribe(l: Listener): void {
    this.listeners.push(l);
  }

  private notify(): void {
    for (const l of this.listeners) l(this.state);
  }
}

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy: "Easy",
  standard: "Standard",
  hard: "Hard",
  challenge: "Challenge",
};
